import { Icona } from '../../components/Icona';
import { tonoStato } from '../../lib/stati';
import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { commesse } from '../../api/commesse';
import type { DettaglioCommessa, PersonaDaScegliere } from '../../api/commesse';
import { formattaData, formattaImporto, formattaNumero, formattaPercentuale } from '../../lib/formato';
import { numeroDecimale } from '../../lib/numeri';
import { usePuo } from '../sessione/ContestoSessione';
import { apri } from '../../lib/ancora';
import { NuovaRichiesta } from '../acquisti/NuovaRichiesta';

interface Props {
  commessaId: string;
  onChiudi: () => void;
}

export function SchedaCommessa({ commessaId, onChiudi }: Props) {
  const [dettaglio, setDettaglio] = useState<DettaglioCommessa | null>(null);
  const [errore, setErrore] = useState<string | null>(null);
  /*  Cambiare i dati di cantiere cambia anche i numeri della scheda — i chilometri
      finiranno nelle trasferte — quindi dopo un salvataggio si rilegge tutto invece di
      aggiornare a mano il pezzo toccato: due verità sullo stesso schermo divergono
      sempre, e sempre nel momento peggiore.  */
  const [versione, setVersione] = useState(0);

  useEffect(() => {
    let annullato = false;
    setDettaglio(null);
    setErrore(null);
    commesse
      .leggi(commessaId)
      .then((d) => !annullato && setDettaglio(d))
      .catch((e: Error) => !annullato && setErrore(e.message));
    return () => {
      annullato = true;
    };
  }, [commessaId, versione]);

  useEffect(() => {
    const chiudiConEsc = (e: KeyboardEvent) => e.key === 'Escape' && onChiudi();
    window.addEventListener('keydown', chiudiConEsc);
    return () => window.removeEventListener('keydown', chiudiConEsc);
  }, [onChiudi]);

  if (errore) {
    return (
      <aside className="scheda">
        <p className="errore">{errore}</p>
        <button type="button" onClick={onChiudi}>
          Chiudi
        </button>
      </aside>
    );
  }

  if (!dettaglio) {
    return (
      <aside className="scheda">
        <p className="attesa">Carico la commessa…</p>
      </aside>
    );
  }

  return (
    <CorpoCommessa
      dettaglio={dettaglio}
      onChiudi={onChiudi}
      onCantiereSalvato={() => setVersione((v) => v + 1)}
    />
  );
}

/*  Il corpo separato dal caricamento, ed esportato, per una ragione precisa: così si può
    disegnare con dei dati in mano e leggere quello che esce, senza un browser e senza un
    database. I difetti trovati finora su questa interfaccia — pezzi di testo che si
    attaccavano, un plurale sbagliato, un elenco che restava aperto — erano tutti
    invisibili ai test e visibili al primo sguardo: questo è il modo più economico di dare
    quello sguardo a ogni build.  */
type SezioneCommessa = 'riepilogo' | 'ordini' | 'richieste' | 'ore';

/*  Le sezioni della scheda, con quante cose ci sono dentro: «Ordini 12» dice se vale la
    pena aprire prima di aprire.  */
const SEZIONI: { chiave: SezioneCommessa; etichetta: string; quanti: (d: DettaglioCommessa) => number }[] = [
  { chiave: 'riepilogo', etichetta: 'Riepilogo', quanti: () => 0 },
  { chiave: 'ordini', etichetta: 'Ordini', quanti: (d) => d.ordini.length },
  { chiave: 'richieste', etichetta: 'Da comprare', quanti: (d) => d.daComprare.length },
  { chiave: 'ore', etichetta: 'Ore', quanti: (d) => d.ore.length },
];

export function CorpoCommessa({
  dettaglio,
  onChiudi,
  onCantiereSalvato,
}: {
  dettaglio: DettaglioCommessa;
  onChiudi: () => void;
  /** Assente nei test e in chi disegna la scheda con dati finti: allora non si salva niente. */
  onCantiereSalvato?: () => void;
}) {
  const { consuntivo: k, ordini, daComprare, ore } = dettaglio;
  const [sezione, setSezione] = useState<SezioneCommessa>('riepilogo');

  return (
    <aside className="scheda" aria-label={`Commessa ${k.numero ?? ''}`}>
      <header>
        <div>
          <p className="chiave">
            <strong>{k.numero ?? 'senza numero'}</strong>
            <span className={`stato stato-${k.stato.toLowerCase().replace(' ', '-')}`}>{k.stato}</span>
            {k.oltreLaFinePrevista && <span className="stato in-ritardo">oltre la consegna</span>}
          </p>
          <h2>{k.descrizione}</h2>
          <p className="riferimenti">
            {k.cliente ?? k.codiceClienteMago}
            {/*  L'offerta da cui e' nata, e si apre. Un riferimento che si legge e non si
                 apre costa a chi legge di cercarlo a mano nella griglia accanto — ed e'
                 la domanda che ci si fa piu' spesso guardando una commessa: «quanto
                 avevamo detto».  */}
            {k.preventivoOrigineId !== null && (
              <>
                {' · nata da '}
                <button
                  type="button"
                  className="collegamento"
                  onClick={() => apri('preventivi', k.preventivoOrigineId!)}
                >
                  offerta {k.numeroPreventivo ?? 'senza numero'}
                </button>
              </>
            )}
            {k.numeroOrdineCliente && ` · suo ordine ${k.numeroOrdineCliente}`}
            {k.finePrevista && ` · fine prevista ${formattaData(k.finePrevista)}`}
            {k.giorniAllaFine !== null && (
              <> ({k.giorniAllaFine < 0 ? `${-k.giorniAllaFine} giorni fa` : `fra ${k.giorniAllaFine} giorni`})</>
            )}
          </p>
        </div>
        <button type="button" className="chiudi" onClick={onChiudi} aria-label="Chiudi la scheda">
          <Icona nome="chiudi" />
        </button>
      </header>

      <Responsabile consuntivo={k} onSalvato={onCantiereSalvato} />
      <DaFatturare consuntivo={k} onSalvato={onCantiereSalvato} />
      <Cantiere consuntivo={k} onSalvato={onCantiereSalvato} />

      {/*  LA SCHEDA A SEZIONI, 16/10/2026: «al posto di una pagina sola, un menu sopra dove
           hai ordini, RDA, e scorri» — con mille ordini una pagina sola non si scorre. Il
           riepilogo resta la prima: e' quella che si guarda piu' spesso. Le altre sezioni
           non si smontano, si nascondono, cosi' tornandoci si ritrova tutto com'era.  */}
      <nav className="sezioni-commessa" aria-label="Sezioni della commessa">
        {SEZIONI.map((v) => (
          <button
            key={v.chiave}
            type="button"
            className={sezione === v.chiave ? 'on' : ''}
            aria-pressed={sezione === v.chiave}
            onClick={() => setSezione(v.chiave)}
          >
            {v.etichetta}
            {v.quanti(dettaglio) > 0 && <em className="quanti">{v.quanti(dettaglio)}</em>}
          </button>
        ))}
      </nav>

      <div className="sezione-commessa" hidden={sezione !== 'riepilogo'}>
        <Margine consuntivo={k} />
        <Attenzione consuntivo={k} />
        <BarraCosti consuntivo={k} />

        <table className="consuntivo">
          <thead>
            <tr>
              <th>Voce</th>
              <th className="destra">Budget</th>
              <th className="destra">Previsto</th>
              <th className="destra">Sul budget</th>
            </tr>
          </thead>
          <tbody>
            <Voce
              nome="Materiali"
              budget={k.budgetMateriali}
              previsto={k.materialiPrevisti}
              quota={k.materialiSulBudget}
              spiegazione={
                <>
                  {formattaImporto(k.materialiRicevuti)} già arrivati,{' '}
                  {formattaImporto(k.materialiInArrivo)} ordinati e in arrivo,{' '}
                  {formattaImporto(k.materialiDaOrdinare)} ancora da comprare
                  {/*  Il rinunciato si nomina solo quando c'è, e si dice che è fuori dal
                       conto: altrimenti è un numero in più che sembra dover quadrare.  */}
                  {k.materialiChiusi > 0 && (
                    <>
                      {' · '}
                      {formattaImporto(k.materialiChiusi)} di residuo chiuso, fuori dal conto
                    </>
                  )}
                </>
              }
            />
            {/*  «Manodopera» e non «Ore»: il suo budget e' in euro come gli altri due, e la
                 percentuale confronta il costo delle ore col budget — euro con euro. Le ore
                 lavorate stanno nella spiegazione, dove sono un dato e non un denominatore. */}
            <Voce
              nome="Manodopera"
              budget={k.budgetOre}
              previsto={k.costoOreStimato}
              quota={k.oreSulBudget}
              spiegazione={
                <>
                  {formattaNumero(k.oreLavorate)} ore da rapportini approvati, valorizzate al
                  costo orario di oggi
                </>
              }
            />
            <Voce
              nome="Trasferte"
              budget={k.budgetTrasferte}
              previsto={k.trasferte}
              quota={k.trasferteSulBudget}
              spiegazione={
                k.trasferte > 0 ? (
                  <>chilometri e indennità dei rapportini approvati</>
                ) : (
                  <em>nessuna trasferta dichiarata sui rapportini approvati</em>
                )
              }
            />
          </tbody>
          <tfoot>
            <tr>
              <td>Totale</td>
              <td className="destra">{formattaImporto(k.budgetCosti)}</td>
              <td className="destra">{formattaImporto(k.costiPrevisti)}</td>
              <td className="destra">—</td>
            </tr>
          </tfoot>
        </table>

        <Cantiere consuntivo={k} onSalvato={onCantiereSalvato} />
      </div>

      <div className="sezione-commessa" hidden={sezione !== 'ordini'}>
        <Elenco
          titolo={
            ordini.length === 1 ? 'Un ordine su questa commessa' : `${ordini.length} ordini su questa commessa`
          }
          vuoto="Nessun ordine emesso per questa commessa."
          quanti={ordini.length}
        >
          <table className="confronto">
            <thead>
              <tr>
                <th>Ordine</th>
                <th className="destra">Impegnato</th>
                <th className="destra">Ricevuto</th>
              </tr>
            </thead>
            <tbody>
              {/*  LE CONSEGNE A COLORI, punto 8 della riunione del 14/09/2026: «ritardo
                   rosso, ricevuto verde».

                   Le parole c'erano gia' tutte — «consegna parziale», «in ritardo» — ma in
                   grigio, in fondo a una riga di contorno lunga sei voci, e su una commessa
                   con dodici ordini nessuno le leggeva. Adesso sono le pastiglie che
                   l'elenco usa gia' per gli stati: stesso verde, stessa ambra, stesso
                   rosso, cosi' chi guarda la griglia e poi apre la commessa non deve
                   imparare due alfabeti.

                   In ritardo la riga prende anche una barra rossa sul fianco: le pastiglie
                   si leggono una alla volta, la barra si vede scorrendo.  */}
              {ordini.map((o) => {
                /*  «Arrivato tutto» si deduce dal tono e non dal confronto con la stringa
                    'Completa': il nome dello stato lo decide il backend, il tono lo decide
                    `stati.ts`, e un posto solo che traduce e' meglio di due.  */
                const arrivatoTutto = tonoStato(o.consegna) === 'buono';

                return (
                  <tr key={o.id} className={o.inRitardo ? 'in-ritardo' : ''}>
                    <td>
                      <span className="nome">
                        {o.numero ?? 'senza numero'} — {o.fornitore ?? o.codiceFornitoreMago}
                      </span>
                      <span className="dettaglio elenco">
                        <span>del {formattaData(o.dataOrdine)}</span>
                        <span className={`pastiglia ${tonoStato(o.consegna)}`}>
                          consegna {o.consegna.toLowerCase()}
                        </span>
                        {/*  «prevista» e non «attesa»: lo stato della consegna può essere
                             «Attesa», e nella stessa riga la stessa parola finiva per dire
                             due cose diverse — «consegna parziale attesa 28/10/2026».  */}
                        {o.consegnaAttesa && <span>prevista {formattaData(o.consegnaAttesa)}</span>}
                        {/*  Il residuo chiuso spiega l'unico divario che sembra un errore:
                             un ordine concluso con il ricevuto più basso dell'impegnato.  */}
                        {o.chiuso > 0 && <span>chiuso {formattaImporto(o.chiuso)}</span>}
                        {o.inRitardo && <span className="pastiglia critico">in ritardo</span>}
                        <span>
                          {o.righe} {o.righe === 1 ? 'riga' : 'righe'}
                        </span>
                      </span>
                    </td>
                    <td className="destra">{formattaImporto(o.impegnato)}</td>
                    <td className={`destra${arrivatoTutto ? ' arrivato' : ''}`}>
                      {o.ricevuto > 0 ? formattaImporto(o.ricevuto) : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Elenco>
      </div>

      <div className="sezione-commessa" hidden={sezione !== 'richieste'}>

        {/*  Da qui si apre una richiesta d'acquisto (16/10/2026): sulla commessa, dove
             il capocantiere sa cosa serve. Su una commessa chiusa non si compra piu'.  */}
        {k.stato !== 'Chiusa' && (
          <NuovaRichiesta commessaId={k.id} descrizioneCommessa={k.descrizione} />
        )}

        <Elenco
          titolo={
            daComprare.length === 1
              ? 'Una riga ancora da comprare'
              : `${daComprare.length} righe ancora da comprare`
          }
          vuoto="Niente in attesa di essere comprato."
          quanti={daComprare.length}
        >
          <table className="confronto">
            <thead>
              <tr>
                <th>Cosa</th>
                <th className="destra">Residuo</th>
                <th className="destra">Valore</th>
              </tr>
            </thead>
            <tbody>
              {daComprare.map((r) => (
                /*  Era `fuori`, che nel resto dell'applicativo vuol dire «smorzata»: e' il
                     trattamento delle righe opzionali e dei «di cui», cioe' di quello che si
                     puo' saltare. Su una riga in ritardo era esattamente il contrario di
                     quello che serve — la rendeva meno visibile delle altre.  */
                <tr key={r.id} className={r.inRitardo ? 'in-ritardo' : ''}>
                  <td>
                    <span className="nome">{r.descrizione}</span>
                    <span className="dettaglio elenco">
                      <span>richiesta {r.numeroRichiesta ?? 'in bozza'}</span>
                      <span>serve il {formattaData(r.dataFabbisogno)}</span>
                      {r.inRitardo && <span className="pastiglia critico">in ritardo</span>}
                      {r.migliorCostoOggi !== null && (
                        <span>
                          miglior costo oggi {formattaImporto(r.migliorCostoOggi)}
                          {r.migliorFornitoreOggi && ` da ${r.migliorFornitoreOggi}`}
                        </span>
                      )}
                      {r.costoStimato === null && <em className="motivo">nessun costo stimato</em>}
                    </span>
                  </td>
                  <td className="destra">
                    {formattaNumero(r.daOrdinare)} {r.unitaMisura}
                  </td>
                  <td className="destra">{r.costoStimato === null ? '—' : formattaImporto(r.valore)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Elenco>
      </div>

      <div className="sezione-commessa" hidden={sezione !== 'ore'}>
        <Elenco
          titolo={ore.length === 1 ? 'Le ore di una persona' : `Le ore di ${ore.length} persone`}
          vuoto="Nessun rapportino approvato su questa commessa."
          quanti={ore.length}
        >
          <table className="confronto">
            <thead>
              <tr>
                <th>Chi</th>
                <th className="destra">Ore</th>
                <th className="destra">Costo</th>
              </tr>
            </thead>
            <tbody>
              {ore.map((o) => (
                <tr key={o.operaio ?? 'senza nome'}>
                  <td>
                    <span className="nome">{o.operaio ?? 'operaio non in anagrafica'}</span>
                    <span className="dettaglio elenco">
                      <span>
                        {o.rapportini} {o.rapportini === 1 ? 'rapportino' : 'rapportini'}
                      </span>
                      {o.dal && (
                        <span>
                          dal {formattaData(o.dal)}
                          {o.al && o.al !== o.dal && ` al ${formattaData(o.al)}`}
                        </span>
                      )}
                    </span>
                  </td>
                  <td className="destra">{formattaNumero(o.ore)}</td>
                  <td className="destra">{formattaImporto(o.costo)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Elenco>
      </div>
    </aside>
  );
}

/*  Il cantiere: dove si lavora e quanto dista.

    Due campi e una mappa, ma è la prima parte scrivibile di questa scheda, e le scritture
    hanno regole che le letture non hanno. Chi non ha il permesso li vede e basta — il
    server rifiuterebbe comunque, questo serve a non mostrare bottoni che danno 403.

    Il bottone di Google PROPONE. I chilometri finiscono nel campo, dove restano
    modificabili, e vengono salvati solo se qualcuno preme «Salva»: il numero che diventa
    un costo l'ha guardato una persona. Quando il calcolo non si può fare — chiave
    mancante, indirizzo sbagliato, server senza rete — la frase che torna dal server lo
    dice, e i chilometri si scrivono a mano. Non è un errore dell'applicativo: è come si
    lavora finché le chiavi non ci sono.

    La mappa è un iframe della Embed API di Google, che vuole l'indirizzo come testo:
    nessuna coordinata da chiedere, da pagare e da conservare. Senza chiave non compare, e
    la scheda lo dice invece di lasciare un rettangolo bianco.  */
/*  CHI SEGUE LA COMMESSA (16/10/2026). Nasce dal tecnico responsabile dell'offerta; si
    cambia da qui, e si salva alla scelta — e' una decisione sola. E' quello su cui si
    filtra «le mie commesse», e a chi andranno gli avvisi della commessa.  */
function Responsabile({
  consuntivo: k,
  onSalvato,
}: {
  consuntivo: DettaglioCommessa['consuntivo'];
  onSalvato?: () => void;
}) {
  const puoFare = usePuo();
  const modificabile = puoFare('GestisciCommesse') && onSalvato !== undefined;
  const [persone, setPersone] = useState<PersonaDaScegliere[] | null>(null);
  const [errore, setErrore] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);

  useEffect(() => {
    if (!modificabile) return;
    commesse
      .responsabili()
      .then(setPersone)
      .catch((e: Error) => setErrore(e.message));
  }, [modificabile]);

  async function scegli(id: string) {
    setInCorso(true);
    setErrore(null);
    try {
      await commesse.impostaResponsabile(k.id, id === '' ? null : id);
      onSalvato?.();
    } catch (e) {
      setErrore((e as Error).message);
    } finally {
      setInCorso(false);
    }
  }

  return (
    <p className="responsabile-commessa">
      <span className="etichetta">Responsabile</span>{' '}
      {modificabile && persone !== null ? (
        <select
          aria-label="Responsabile della commessa"
          value={k.responsabileId ?? ''}
          disabled={inCorso}
          onChange={(e) => void scegli(e.target.value)}
        >
          <option value="">nessuno</option>
          {/*  Se il responsabile di oggi non e' piu' fra le utenze attive resta
               leggibile: sparire dalla tendina non deve sembrare «nessuno».  */}
          {k.responsabileId !== null && !persone.some((p) => p.id === k.responsabileId) && (
            <option value={k.responsabileId}>{k.responsabile ?? 'utenza non attiva'}</option>
          )}
          {persone.map((p) => (
            <option key={p.id} value={p.id}>
              {p.nome}
            </option>
          ))}
        </select>
      ) : (
        <strong>{k.responsabile ?? 'nessuno'}</strong>
      )}
      {errore && <em className="errore"> {errore}</em>}
    </p>
  );
}

/*  DA FATTURARE (16/10/2026): «questa l'ho finita, da fatturare: alla mano arriva la
    notifica». La segna chi segue il cantiere; la toglie chi fattura, a fattura fatta.
    Segnarla e' una responsabilita' — per questo chiede conferma, e dice chi e quando.  */
function DaFatturare({
  consuntivo: k,
  onSalvato,
}: {
  consuntivo: DettaglioCommessa['consuntivo'];
  onSalvato?: () => void;
}) {
  const puoFare = usePuo();
  const [conferma, setConferma] = useState(false);
  const [inCorso, setInCorso] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);

  async function esegui(azione: () => Promise<void>) {
    setInCorso(true);
    setErrore(null);
    try {
      await azione();
      setConferma(false);
      onSalvato?.();
    } catch (e) {
      setErrore((e as Error).message);
    } finally {
      setInCorso(false);
    }
  }

  if (k.daFatturareIl !== null) {
    return (
      <p className="da-fatturare segnata">
        <strong>Da fatturare</strong> dal {formattaData(k.daFatturareIl.slice(0, 10))}
        {k.daFatturareDa && <>, segnata da {k.daFatturareDa}</>}.
        {puoFare('FatturaCommesse') && onSalvato !== undefined && (
          <button
            type="button"
            className="collegamento"
            disabled={inCorso}
            onClick={() => void esegui(() => commesse.togliDaFatturare(k.id))}
          >
            togli il segno
          </button>
        )}
        {errore && <em className="errore"> {errore}</em>}
      </p>
    );
  }

  if (!puoFare('GestisciCommesse') || onSalvato === undefined || k.stato === 'Chiusa') return null;

  return (
    <p className="da-fatturare">
      {conferma ? (
        <>
          Avvisa chi fa le fatture che questa commessa si può fatturare?{' '}
          <button
            type="button"
            className="primario"
            disabled={inCorso}
            onClick={() => void esegui(() => commesse.segnaDaFatturare(k.id))}
          >
            Sì, da fatturare
          </button>
          <button type="button" disabled={inCorso} onClick={() => setConferma(false)}>
            No
          </button>
        </>
      ) : (
        <button type="button" onClick={() => setConferma(true)}>
          Segna da fatturare
        </button>
      )}
      {errore && <em className="errore"> {errore}</em>}
    </p>
  );
}

function Cantiere({
  consuntivo: k,
  onSalvato,
}: {
  consuntivo: DettaglioCommessa['consuntivo'];
  onSalvato?: () => void;
}) {
  const puoFare = usePuo();
  const modificabile = puoFare('GestisciCommesse') && onSalvato !== undefined;

  const [indirizzo, setIndirizzo] = useState(k.indirizzoCantiere ?? '');
  const [km, setKm] = useState(k.kmTrasferta === null ? '' : String(k.kmTrasferta));
  const [messaggio, setMessaggio] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);
  const [chiaveMappa, setChiaveMappa] = useState<string | null>(null);

  /*  L'indirizzo su cui i chilometri a schermo sono stati calcolati: serve a non
      richiamare Google quando si entra e si esce dal campo senza toccarlo.  */
  const ultimoCalcolato = useRef<string | null>(k.indirizzoCantiere ?? null);

  useEffect(() => {
    let annullato = false;
    commesse
      .configurazioneMappe()
      .then((c) => !annullato && setChiaveMappa(c.chiaveBrowser))
      // Senza chiave la mappa non si mostra: non è un errore da sbattere in faccia.
      .catch(() => undefined);
    return () => {
      annullato = true;
    };
  }, []);

  /*  Come sul preventivo: i chilometri si ricalcolano da soli quando l'indirizzo cambia,
      e il calcolo usa l'indirizzo CHE SI HA DAVANTI e non quello salvato. Calcolare su
      quello salvato dopo averlo cambiato e' peggio che non calcolare: il numero c'e',
      sembra giusto, e riguarda un altro posto.

      Non a ogni tasto — ogni chiamata si paga — ma quando si esce dal campo. Ricalcola e
      non salva: Google propone, il tecnico guarda e preme Salva.  */
  function ricalcolaSeCambiato(nuovo: string) {
    const pulito = nuovo.trim();
    if (pulito === '' || pulito === (ultimoCalcolato.current ?? '').trim()) return;
    void calcola(pulito);
  }

  async function calcola(indirizzoDaUsare?: string) {
    const cercato = (indirizzoDaUsare ?? indirizzo).trim();
    setInCorso(true);
    setMessaggio(null);
    ultimoCalcolato.current = cercato;
    try {
      const esito = await commesse.calcolaKm(k.id, cercato === '' ? null : cercato);
      if (esito.kmAndataRitorno === null) setMessaggio(esito.problema ?? 'Calcolo non riuscito.');
      else {
        setKm(String(esito.kmAndataRitorno));
        setMessaggio(
          `${esito.kmAndataRitorno} km andata e ritorno da ${esito.sede}. Controlla e salva.`,
        );
      }
    } catch (errore) {
      setMessaggio((errore as Error).message);
    } finally {
      setInCorso(false);
    }
  }

  async function salva() {
    setInCorso(true);
    setMessaggio(null);
    try {
      await commesse.impostaCantiere(k.id, {
        indirizzoCantiere: indirizzo.trim() === '' ? null : indirizzo.trim(),
        kmTrasferta: numeroDecimale(km),
      });
      onSalvato?.();
    } catch (errore) {
      setMessaggio((errore as Error).message);
    } finally {
      setInCorso(false);
    }
  }

  const indirizzoMostrato = modificabile ? indirizzo.trim() : (k.indirizzoCantiere ?? '');

  return (
    <section className="cantiere">
      <h3>Cantiere</h3>

      {modificabile ? (
        <div className="campi">
          {/*  Le stesse due file della scheda preventivo: l'indirizzo e' una frase e si
               prende la riga, i chilometri sono un numero e stanno sotto.  */}
          <div className="fila testi">
            <label>
              <span>Indirizzo del cantiere</span>
              <input
                className="campo"
                value={indirizzo}
                placeholder="Via, numero, CAP, città e provincia"
                onChange={(e) => setIndirizzo(e.target.value)}
                onBlur={(e) => ricalcolaSeCambiato(e.target.value)}
              />
            </label>
          </div>

          <div className="fila numeri">
            <label className="stretto">
              <span>Km andata e ritorno</span>
              <input
                className="campo"
                inputMode="decimal"
                value={km}
                onChange={(e) => setKm(e.target.value)}
              />
            </label>
          </div>

          <div className="comandi">
            <button type="button" onClick={() => void calcola()} disabled={inCorso}>
              Calcola con Google
            </button>
            <button type="button" className="primario" onClick={salva} disabled={inCorso}>
              Salva
            </button>
          </div>
        </div>
      ) : (
        <p className="dettaglio">
          {k.indirizzoCantiere ?? 'Indirizzo non indicato'}
          {' · '}
          {k.kmTrasferta === null
            ? 'chilometri non stabiliti'
            : `${formattaNumero(k.kmTrasferta)} km andata e ritorno`}
        </p>
      )}

      {messaggio && <p className="nota">{messaggio}</p>}

      {indirizzoMostrato !== '' &&
        (chiaveMappa ? (
          <iframe
            className="mappa"
            title={`Mappa di ${indirizzoMostrato}`}
            loading="lazy"
            /*  La chiave del browser è ristretta per referrer sulla console di Google:
                perché la restrizione funzioni, il referrer deve partire.  */
            referrerPolicy="no-referrer-when-downgrade"
            src={`https://www.google.com/maps/embed/v1/place?key=${encodeURIComponent(
              chiaveMappa,
            )}&q=${encodeURIComponent(indirizzoMostrato)}&language=it`}
          />
        ) : (
          <p className="nota">
            La mappa non si vede finché non è configurata la chiave di Google
            (<code>Mappe:ChiaveBrowser</code>).
          </p>
        ))}
    </section>
  );
}

/**
 * Il margine previsto in cima, perché è la domanda che porta qualcuno ad aprire una
 * commessa. Dice anche che è il migliore dei casi: le trasferte non ci sono dentro.
 */
function Margine({ consuntivo: k }: { consuntivo: DettaglioCommessa['consuntivo'] }) {
  if (k.prezzoConcordato <= 0)
    return (
      <p className="verdetto assente">
        Nessun prezzo concordato: senza quello il margine non si può calcolare, e restano
        solo i costi.
      </p>
    );

  const negativo = k.marginePrevisto < 0;

  return (
    <p className={`verdetto${negativo ? ' allarme' : ''}`}>
      Margine previsto <strong>{formattaImporto(k.marginePrevisto)}</strong>
      {k.marginePercentuale !== null && <> ({formattaPercentuale(k.marginePercentuale)})</>} su{' '}
      {formattaImporto(k.prezzoConcordato)} concordati.{' '}
      {negativo
        ? 'La commessa è già in perdita con i costi che si vedono adesso.'
        : 'È il migliore dei casi: quello che non è ancora comprato né dichiarato non c’è dentro.'}
    </p>
  );
}

/*  Dove finiscono i soldi concordati, su una scala sola.

    La tabella qui sotto dice gli stessi numeri, e li dice meglio: questa barra serve a una
    cosa che una tabella non fa, cioè far vedere le PROPORZIONI — quanto del prezzo è già
    speso, quanto è promesso, quanto resta — in un colpo d'occhio, prima di leggere le
    cifre.

    Cinque pezzi di costo nell'ordine in cui diventano certi: materiale arrivato,
    materiale ordinato e non ancora arrivato, materiale chiesto e non ancora comprato, ore
    registrate, trasferte dichiarate. Ci sono tutti e cinque perché insieme devono fare
    esattamente i costi previsti: un pezzo che manca lascia un buco nella traccia e
    gonfia il margine di quello che il buco vale.

    Nessun pezzo «a finire»: la manodopera che manca sarebbe il budget ore
    residuo, cioè un'ipotesi, e una barra che mescola fatti e ipotesi senza dirlo è peggio
    di nessuna barra. Quello che resta fino al prezzo concordato è il margine, e la scheda
    dice già che è il migliore dei casi.

    La scala è il prezzo concordato, tranne quando i costi lo superano: allora è il costo,
    e una tacca segna dove stava il prezzo — la barra piena si vede da lontano, ed è il
    caso in cui serve vederla da lontano.  */
function BarraCosti({ consuntivo: k }: { consuntivo: DettaglioCommessa['consuntivo'] }) {
  if (k.prezzoConcordato <= 0) return null;

  const base = Math.max(k.prezzoConcordato, k.costiPrevisti);
  const quota = (valore: number) => `${(valore / base) * 100}%`;

  const pezzi = [
    { classe: 'ricevuti', nome: 'arrivato', valore: k.materialiRicevuti },
    { classe: 'in-arrivo', nome: 'in arrivo', valore: k.materialiInArrivo },
    { classe: 'da-ordinare', nome: 'da comprare', valore: k.materialiDaOrdinare },
    { classe: 'ore', nome: 'ore registrate', valore: k.costoOreStimato },
    { classe: 'trasferte', nome: 'trasferte', valore: k.trasferte },
  ].filter((p) => p.valore > 0);

  const margine = Math.max(0, k.prezzoConcordato - k.costiPrevisti);
  const oltre = k.costiPrevisti > k.prezzoConcordato;

  return (
    <div className="barra-costi">
      <div
        className="traccia"
        role="img"
        aria-label={`Su ${formattaImporto(k.prezzoConcordato)} concordati: ${formattaImporto(
          k.costiPrevisti,
        )} di costi previsti, ${formattaImporto(margine)} di margine.`}
      >
        {pezzi.map((p) => (
          <span key={p.classe} className={p.classe} style={{ width: quota(p.valore) }} />
        ))}
        {margine > 0 && <span className="margine" style={{ width: quota(margine) }} />}

        {/*  Il budget sta sulla stessa scala del prezzo: è l'unico modo di vedere insieme
             «quanto ho speso» e «quanto avevo previsto di spendere».  */}
        {k.budgetCosti > 0 && k.budgetCosti <= base && (
          <span className="tacca budget" style={{ left: quota(k.budgetCosti) }}>
            <em>budget {formattaImporto(k.budgetCosti)}</em>
          </span>
        )}
        {oltre && (
          <span className="tacca oltre" style={{ left: quota(k.prezzoConcordato) }}>
            <em>prezzo concordato</em>
          </span>
        )}
      </div>

      <p className="legenda">
        {pezzi.map((p) => (
          <span key={p.classe}>
            <i className={p.classe} aria-hidden="true" />
            {p.nome}
          </span>
        ))}
        {margine > 0 && (
          <span>
            <i className="margine" aria-hidden="true" />
            margine
          </span>
        )}
      </p>
    </div>
  );
}

/*  Le cose che su questa commessa chiedono attenzione, in una riga sola.

    Sta sotto il margine e non in fondo perché è l'altra metà della domanda che porta ad
    aprire una commessa: quanto ci resta, e cosa mi sta scappando. Sono quattro cose che
    si scoprono solo aprendo quattro elenchi diversi — un ordine che doveva arrivare la
    settimana scorsa, merce respinta al ricevimento che nessuno ha riordinato, una
    richiesta compilata e mai inviata, materiale scritto a mano senza codice che non entra
    nei costi.

    Quando non c'è niente da segnalare la riga non compare: una riga verde che dice «tutto
    a posto» si smette di leggere, e allora si smette di leggerla anche il giorno in cui
    diventa rossa.  */
function Attenzione({ consuntivo: k }: { consuntivo: DettaglioCommessa['consuntivo'] }) {
  const voci = [
    k.ordiniInRitardo > 0 &&
      `${k.ordiniInRitardo} ${k.ordiniInRitardo === 1 ? 'ordine in ritardo' : 'ordini in ritardo'}`,
    k.righeRespinte > 0 &&
      `${k.righeRespinte} ${k.righeRespinte === 1 ? 'riga respinta' : 'righe respinte'} al ricevimento`,
    k.richiesteFerme > 0 &&
      `${k.richiesteFerme} ${k.richiesteFerme === 1 ? 'richiesta ferma' : 'richieste ferme'} da oltre tre giorni`,
    k.materialiDaCodificare > 0 &&
      `${k.materialiDaCodificare} ${k.materialiDaCodificare === 1 ? 'materiale' : 'materiali'} da codificare`,
  ].filter((v): v is string => typeof v === 'string');

  if (voci.length === 0) return null;

  return (
    <p className="verdetto attenzione">
      <strong>Chiede attenzione:</strong> {voci.join(' · ')}.
    </p>
  );
}

/** Una voce del consuntivo: budget, previsto, e quanto ne è andato. */
function Voce({
  nome,
  budget,
  previsto,
  quota,
  spiegazione,
}: {
  nome: string;
  budget: number;
  previsto: number | null;
  quota: number | null;
  spiegazione: ReactNode;
}) {
  const sfondato = quota !== null && quota > 100;

  return (
    <tr className={sfondato ? 'sfondato' : ''}>
      <td>
        <span className="nome">{nome}</span>
        <span className="dettaglio">{spiegazione}</span>
      </td>
      <td className="destra">{budget > 0 ? formattaImporto(budget) : '—'}</td>
      <td className="destra">{previsto === null ? '—' : formattaImporto(previsto)}</td>
      <td className="destra">
        {quota === null ? '—' : formattaPercentuale(quota)}
        {/* La barra si ferma al 100% ma la percentuale no: sfondare va detto col numero,
            non con una barra che esce dalla colonna. */}
        {quota !== null && (
          <span className="barra-budget" aria-hidden="true">
            <span style={{ width: `${Math.min(quota, 100)}%` }} />
          </span>
        )}
      </td>
    </tr>
  );
}

/** Una lista che può essere vuota, e quando è vuota lo dice invece di sparire. */
function Elenco({
  titolo,
  vuoto,
  quanti,
  children,
}: {
  titolo: string;
  vuoto: string;
  quanti: number;
  children: ReactNode;
}) {
  if (quanti === 0) return <p className="nota">{vuoto}</p>;

  return (
    /*  Aperto sempre: dal 16/10/2026 ogni elenco sta nella sua sezione, ed e' la sezione
        a decidere se si vede.  */
    <details className="provenienza" open>
      <summary>{titolo}</summary>
      {children}
    </details>
  );
}
